import React, { useState, useEffect } from 'react';
import { Typography, Box, Chip, FormControl, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Modal, IconButton, Divider, CircularProgress } from '@mui/material';
import DePINHeader, { NetworkType } from '../../components/depin/DePINHeader';
import { useApi } from '../../contexts/ApiContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useWithTokenRefresh } from '../../utils/api';
import { toastError, toastSuccess } from '../../utils/toast';
import { ConnectButton } from '@mysten/dapp-kit';
import StorageIcon from '@mui/icons-material/Storage';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import KeyIcon from '@mui/icons-material/Key';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

// Unified wallet context
import { useUnifiedWeb3Wallet, WalletType } from '../../contexts/unified_wallet/UnifiedWeb3WalletContext';
import { DealInfo } from '@flashbacktech/flashbackclient/stellarv2';

// SUI-specific imports
import { useSuiWallet } from '../../contexts/sui/SuiWalletContext';
import { useSignAndExecuteTransaction } from '@mysten/dapp-kit';
import { Transaction as SuiTransaction, Commands } from '@mysten/sui/transactions';
import DePINHubTutorialLauncher from '../../components/depin/DePINHubTutorialLauncher';
import ReputationControl from '../../components/ReputationControl';
import { ApiTypes } from '@flashbacktech/flashbackclient/api';

// Unified deal type for display
interface UnifiedDeal {
  deal_id: number | string;
  bucket_id: number | string;
  bucket_name?: string;
  consumer_id: string;
  provider_id: string;
  provider?: string;
  region?: string;
  country?: string;
  duration_secs?: bigint;
  agreed_storage_mb: number;
  agreed_egress_mb: number;
  used_storage_mb?: number;
  used_egress_mb?: number;
  price_per_gb_storage: bigint;
  price_per_gb_egress: bigint;
  sla_avg_latency_ms: number;
  sla_avg_uptime_pct: number;
  unpaid_storage_mb?: number;
  unpaid_egress_mb?: number;
  api_compatibility: string;
  status: string;
  start_time?: number;
  end_time?: number;
  fb_repo_id?: string;
  balance_consumer?: bigint | number;
  balance_provider?: bigint | number;
  consumer_reputation?: number;
  provider_reputation?: number;
}

// Deal status options (contract-aligned: Pending, not Proposed)
const DEAL_STATUS_OPTIONS = ['All', 'Pending', 'Accepted', 'Rejected', 'Funded', 'Active', 'Completed', 'Cancelled', 'BreachedConsumer', 'BreachedProvider'];

// Contract DealStatus enum: 0=Pending, 1=Accepted, 2=Funded, 3=Completed, 4=Cancelled, 5=BreachedConsumer, 6=BreachedProvider
const STATUS_NAMES = ['Pending','Accepted','Funded','Completed','Cancelled','BreachedConsumer','BreachedProvider'];
const normalizeStatus = (status: string | number | undefined): string =>
  typeof status === 'number' && status >= 0 && status < STATUS_NAMES.length
    ? STATUS_NAMES[status]
    : String(status ?? '');

function DePINHubDeals() {
  const { apiClient } = useApi();
  const { isAuthenticated, globalData } = useAuth();
  const selectedWorkspaceId = globalData?.selectedWorkspaceId || '';
  const navigate = useNavigate();
  const withTokenRefresh = useWithTokenRefresh();

  // Unified wallet context to detect network and connection status
  const {
    walletType,
    isConnected: unifiedIsConnected,
    walletAddress: unifiedWalletAddress,
    suiClient: unifiedSuiClient,
    getBalances: unifiedGetBalances,
    stellarClient,
    connectWallet,
    loading: walletLoading
  } = useUnifiedWeb3Wallet();
  const network: NetworkType = walletType === WalletType.STELLAR ? 'stellar' : walletType === WalletType.SUI ? 'sui' : null;

  // SUI wallet context (for SUI-specific operations)
  const suiWallet = useSuiWallet();
  const { mutate: signAndExecuteTransaction } = useSignAndExecuteTransaction();

  // Use unified SUI client if available, fallback to suiWallet client
  const suiClient = unifiedSuiClient || suiWallet.client;

  // Use unified connection status and wallet address
  const isConnected = unifiedIsConnected;
  const walletAddress = unifiedWalletAddress;

  // State
  const [deals, setDeals] = useState<UnifiedDeal[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Tutorial is always visible
  const showTutorial = true;
  const [statusFilter, setStatusFilter] = useState('All');
  const [viewMode, setViewMode] = useState<'consumer' | 'provider'>('consumer');

  // Action dialogs
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [depositError, setDepositError] = useState('');
  const [selectedDeal, setSelectedDeal] = useState<UnifiedDeal | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Funding modal (for Stellar: consumer funds deal from Accepted -> Funded)
  const [fundingModalOpen, setFundingModalOpen] = useState(false);
  const [fundingAmount, setFundingAmount] = useState('');
  const [fundingDeal, setFundingDeal] = useState<UnifiedDeal | null>(null);
  const [fundingLoading, setFundingLoading] = useState(false);
  const [fundingError, setFundingError] = useState('');

  // Stellar balance for funding validation
  const [stellarBalance, setStellarBalance] = useState<any[]>([]);

  // Helper function to get Provider Registry ID
  const getProviderRegistryId = async (): Promise<string> => {
    // First try environment variable
    const registryId = process.env.REACT_APP_SUI_PROVIDER_REGISTRY_ID;
    
    if (registryId) {
      // Cache it for future use
      localStorage.setItem('sui_provider_registry_id', registryId);
      return registryId;
    }

    // Then try localStorage cache
    const cachedRegistryId = localStorage.getItem('sui_provider_registry_id');
    if (cachedRegistryId) {
      return cachedRegistryId;
    }

    // If neither exists, throw error with helpful message
    throw new Error(
      'ProviderRegistry ID not configured. Please set REACT_APP_SUI_PROVIDER_REGISTRY_ID in your .env file. ' +
      'You can find this ID from the deployment transaction of your provider module.'
    );
  };

  // Helper function to get Provider object ID from provider address
  const getProviderObjectId = async (providerAddress: string): Promise<string> => {
    if (!suiClient) throw new Error('SUI client not available');

    // Get ProviderRegistry ID
    const registryId = await getProviderRegistryId();

    // Get the ProviderRegistry object to access the providers table
    const registry = await suiClient.getObject({
      id: registryId,
      options: { 
        showContent: true,
      }
    });

    if (!registry.data?.content || registry.data.content.dataType !== 'moveObject') {
      throw new Error('Failed to fetch ProviderRegistry');
    }

    const registryFields = (registry.data.content as any).fields;
    const providersTableId = registryFields?.providers?.fields?.id?.id;

    if (!providersTableId) {
      throw new Error('Providers table not found in registry');
    }

    // Query the dynamic field to get the Provider object ID for this address
    const providerField = await suiClient.getDynamicFieldObject({
      parentId: providersTableId,
      name: {
        type: 'address',
        value: providerAddress
      }
    });

    if (!providerField.data?.content || providerField.data.content.dataType !== 'moveObject') {
      throw new Error('Provider not found in registry');
    }

    return (providerField.data.content as any).fields.value;
  };
  // Deal detail modal state
  const [dealDetailOpen, setDealDetailOpen] = useState(false);
  const [detailDeal, setDetailDeal] = useState<UnifiedDeal | null>(null);
  
  // API Key modal state
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [apiKeyStorageType, setApiKeyStorageType] = useState<string>('S3');
  const [apiKeyExisting, setApiKeyExisting] = useState<{ id: string; key: string } | null>(null);
  const [apiKeyNewlyCreated, setApiKeyNewlyCreated] = useState<{ key: string; secret: string } | null>(null);
  const [apiKeyRegenerating, setApiKeyRegenerating] = useState(false);

  // mock state variable for deal data
  const [depositedAmount, setDepositedAmount] = useState<number>(0);

  // Helper: match balance by asset_code (and asset_issuer when configured)
  const matchContractAsset = (b: { asset_type?: string; asset_code?: string; asset_issuer?: string }) => {
    if (b.asset_type === 'native' || !('asset_code' in b)) return false;
    const assetCode = (process.env.REACT_APP_CONTRACT_ASSET || 'USDC').split('#')[0].trim();
    if ((b.asset_code || '').toLowerCase() !== assetCode.toLowerCase()) return false;
    const issuer = process.env.REACT_APP_CONTRACT_ASSET_ISSUER?.trim();
    if (issuer && 'asset_issuer' in b && (b.asset_issuer || '').trim() !== issuer) return false;
    return true;
  };

  // Build balances for header (uses getBalances for Stellar, suiWallet for SUI)
  const getBalances = (): { symbol: string; balance: string | number; color: string }[] => {
    if (network === 'stellar' && isConnected && stellarBalance?.length) {
      const assetCode = (process.env.REACT_APP_CONTRACT_ASSET || 'USDC').split('#')[0].trim();
      const match = stellarBalance.find(matchContractAsset);
      console.log('!!balances', stellarBalance);
      if (match && 'balance' in match) {
        const bal = parseFloat(String(match.balance || 0));
        return [{ symbol: assetCode, balance: bal.toFixed(2), color: '#10B981' }];
      }
      return [];
    }
    if (network === 'sui' && isConnected) {
      return suiWallet.balance?.map(b => ({
        symbol: b.symbol,
        balance: (Number(b.balance) / Math.pow(10, b.decimals)).toFixed(2),
        color: b.symbol === 'SUI' ? '#4DA2FF' : '#10B981',
      })) || [];
    }
    return [];
  };

  // Load deals from smart contract
  const loadDeals = async () => {
    if (!isConnected || !network || !walletAddress) return;

    setLoading(true);
    try {
      let loadedDeals: UnifiedDeal[] = [];

      const addressPrefix = network === 'stellar' ? 'G' : '0x';
      let repos: any[] = [];
      try {
        const reposResponse = await withTokenRefresh(() => apiClient.getStorageRepos(selectedWorkspaceId || undefined, addressPrefix));
        repos = reposResponse?.repos || [];
      } catch (repoErr) {
        console.warn('[Deals] Could not load repos for enrichment:', repoErr);
      }
      console.log('[Deals] Loaded repos for enrichment:', repos.length);

      if (network === 'stellar') {
        if (stellarClient) {
          // Load deals based on view mode
          const response = viewMode === 'consumer'
            ? await stellarClient.deals.getDealsByConsumer(walletAddress)
            : await stellarClient.deals.getDealsByProvider(walletAddress);

          console.log('[Deals] Stellar contract deals:', response.length);
          loadedDeals = response
            .map((dealInfo: DealInfo) => {
            const d = dealInfo.deal;
            const bucket = dealInfo.bucket;
            return {
              deal_id: d.deal_id,
              bucket_id: d.bucket_id,
              bucket_name: bucket?.name ?? '',
              consumer_id: d.consumer_id,
              provider_id: d.provider_id,
              region: bucket?.region,
              country: bucket?.country,
              duration_secs: d.duration_secs,
              agreed_storage_mb: d.agreed_storage_mb,
              agreed_egress_mb: d.agreed_egress_mb,
              used_storage_mb: d.paid_storage_mb,
              used_egress_mb: d.paid_egress_mb,
              unpaid_storage_mb: d.unpaid_storage_mb,
              unpaid_egress_mb: d.unpaid_egress_mb,
              price_per_gb_storage: bucket?.price_per_gb_storage ?? 0n,
              price_per_gb_egress: bucket?.price_per_gb_egress ?? 0n,
              sla_avg_latency_ms: d.sla_avg_latency_ms,
              sla_avg_uptime_pct: d.sla_avg_uptime_pct,
              api_compatibility: d.api_compatibility,
              status: d.status,
              start_time: Number(d.start_ts),
              end_time: undefined, // Not available in Deal type
              fb_repo_id: d.fb_repo_id,
              balance_consumer: d.balance_consumer,
              balance_provider: d.balance_provider,
              consumer_reputation: (() => { const v = typeof dealInfo.consumer_reputation === 'string' ? parseInt(dealInfo.consumer_reputation, 10) : Number(dealInfo.consumer_reputation); return Number.isNaN(v) ? undefined : v; })(),
              provider_reputation: (() => { const v = typeof dealInfo.provider_reputation === 'string' ? parseInt(dealInfo.provider_reputation, 10) : Number(dealInfo.provider_reputation); return Number.isNaN(v) ? undefined : v; })(),
            };
          });
        }
      } else if (network === 'sui') {
        const PACKAGE_ID = process.env.REACT_APP_SUI_PACKAGE_ID || '';
        const EVENT_TYPE = `${PACKAGE_ID}::deal::DealCreated`;

        /* ------------------------------------------------------------- */
        /* 1️⃣ Fetch DealCreated events                                  */
        /* ------------------------------------------------------------- */
        if (!suiClient) return;

        const events = await suiClient.queryEvents({
          query: { MoveEventType: EVENT_TYPE },
          limit: 200, // paginate later if needed
          order: 'descending',
        });

        if (!events.data.length) {
          setDeals([]);
          return;
        }

        /* ------------------------------------------------------------- */
        /* 2️⃣ Filter deals involving this wallet                        */
        /* ------------------------------------------------------------- */
        const relevantDealIds = events.data
          .map((e) => e.parsedJson as any)
          .filter(
            (e) =>
              e.consumer === walletAddress ||
              e.provider === walletAddress
          )
          .map((e) => e.deal_id);

        if (!relevantDealIds.length) {
          setDeals([]);
          return;
        }

        /* ------------------------------------------------------------- */
        /* 3️⃣ Fetch DataUnit objects                                    */
        /* ------------------------------------------------------------- */
        const dealObjects = await suiClient.multiGetObjects({
          ids: relevantDealIds,
          options: { showContent: true, showType: true },
        });

        console.log('Deal objects:', dealObjects);

        /* ------------------------------------------------------------- */
        /* 4️⃣ Normalize → UnifiedDeal                                   */
        /* ------------------------------------------------------------- */
        const statusMap: Record<number, string> = {
          0: 'Active',
          1: 'Completed',
          2: 'Disputed',
          3: 'Refunded',
          4: 'Rejected',
          5: 'Cancelled',
        };

        const bytesToString = (v?: number[]) =>
          Array.isArray(v) ? Buffer.from(v).toString() : '';

        loadedDeals = dealObjects
          .map((obj): UnifiedDeal | null => {
            const data = obj.data;
            if (!data?.content || data.content.dataType !== 'moveObject') {
              return null;
            }

            const f = (data.content as any).fields;

            return {
              deal_id: data.objectId,
              bucket_id: f.provider_id, // Provider object ID
              bucket_name: '',
              consumer_id: f.consumer,
              provider_id: f.provider_id,
              provider: f.provider,

              region: undefined,
              country: undefined,

              duration_secs:
                BigInt(f.duration_days) * BigInt(86400),

              agreed_storage_mb: Number(f.capacity_gb) * 1024,
              agreed_egress_mb: 0,

              used_storage_mb: undefined,
              used_egress_mb: undefined,

              price_per_gb_storage: BigInt(
                f.price_per_gb_per_month
              ),
              price_per_gb_egress: 0n,

              sla_avg_latency_ms: 0,
              sla_avg_uptime_pct: 0,
              api_compatibility: '',

              status: statusMap[Number(f.status)] ?? 'Unknown',

              start_time: Number(f.start_timestamp),
              end_time: Number(f.end_timestamp),

              fb_repo_id: bytesToString(f.fb_deal_id),
            };
          })
          .filter((d): d is UnifiedDeal => d !== null);
      }
  
      setDeals(loadedDeals);
      console.log('[Deals] Loaded deals:', loadedDeals.length, '| Repos for enrichment:', repos.length);
    } catch (error) {
      toastError('Failed to load deals');
      setDeals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && isConnected && network) {
      loadDeals();
    }
  }, [isAuthenticated, isConnected, network, viewMode, stellarClient, suiClient]);

  // Fetch Stellar balance when connected (for header display + funding validation)
  const refreshStellarBalance = async () => {
    if (network !== 'stellar' || !walletAddress) return;
    const getBalancesFn = unifiedGetBalances;
    if (!getBalancesFn) return;
    try {
      const balanceData = await getBalancesFn(walletAddress);
      setStellarBalance(balanceData || []);
    } catch (error) {
      console.error('Failed to fetch Stellar balance:', error);
      setStellarBalance([]);
    }
  };

  useEffect(() => {
    refreshStellarBalance();
  }, [network, walletAddress, unifiedGetBalances]);

  // USDC / configured contract asset balance helpers (Stellar)
  const getContractAssetCode = () =>
    (process.env.REACT_APP_CONTRACT_ASSET || 'USDC').split('#')[0].trim();
  const getUSDCBalance = () => {
    if (!stellarBalance || stellarBalance.length === 0) return 0;
    const match = stellarBalance.find(matchContractAsset);
    return match ? parseFloat(match.balance) : 0;
  };
  const getUSDCAssetCode = () => {
    if (!stellarBalance || stellarBalance.length === 0) return getContractAssetCode();
    const match = stellarBalance.find(matchContractAsset);
    return match?.asset_code ?? getContractAssetCode();
  };

  // Filtered deals based on search and status
  const filteredDeals = deals.filter((deal) => {
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        String(deal.deal_id).toLowerCase().includes(query) ||
        deal.consumer_id.toLowerCase().includes(query) ||
        deal.provider_id.toLowerCase().includes(query) ||
        normalizeStatus(deal.status).toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    // Filter by status (normalize Pending/Proposed)
    if (statusFilter !== 'All') {
      const s = normalizeStatus(deal.status).toLowerCase();
      const f = statusFilter.toLowerCase();
      const match = s === f || (f === 'pending' && s === 'proposed');
      if (!match) return false;
    }

    return true;
  });

  // Format helpers
  const formatPrice = (price: bigint | undefined) => {
    if (price == null || price === undefined) return '$0.00';
    const n = Number(price);
    if (Number.isNaN(n)) return '$0.00';
    return `$${(n / 10000000).toFixed(2)}`;
  };
  const formatUptime = (uptime: number) => {
    const percentage = (uptime / 1000000).toFixed(2);
    return `${percentage}%`;
  };
  const formatAddress = (address: string) => {
    if (!address || address.length < 12) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const getStatusColor = (status: string | number | undefined) => {
    const s = normalizeStatus(status).toLowerCase();
    switch (s) {
      case 'active':
      case 'funded':
        return { bg: '#EAF8F1', color: '#1F8F6B', border: '#D1FAE5' };
      case 'completed':
        return { bg: '#F0F9FF', color: '#0369A1', border: '#BAE6FD' };
      case 'pending':
      case 'proposed':
      case 'accepted':
        return { bg: '#FFF7E6', color: '#C38A00', border: '#FEF3C7' };
      case 'rejected':
      case 'cancelled':
        return { bg: '#FEF2F2', color: '#DC2626', border: '#FEE2E2' };
      case 'breachedconsumer':
      case 'breachedprovider':
        return { bg: '#FEF2F2', color: '#B91C1C', border: '#FEE2E2' };
      default:
        return { bg: '#F3F4F6', color: '#6B7280', border: '#E5E7EB' };
    }
  };

  // Deal detail modal handlers
  const handleDealCardClick = (deal: UnifiedDeal) => {
    setDetailDeal(deal);
    setDealDetailOpen(true);
  };

  const handleCloseDealDetail = () => {
    setDealDetailOpen(false);
    setDetailDeal(null);
  };

  // Helper functions for deal detail modal (kept for potential future use)
  // const formatReputation = (reputation: string | number | undefined) => {
  //   if (!reputation) return 'N/A';
  //   const score = typeof reputation === 'string' ? parseInt(reputation) : reputation;
  //   if (isNaN(score)) return 'N/A';
  //   return score.toFixed(0);
  // };

  // const getReputationColor = (reputation: string | number | undefined) => {
  //   if (!reputation) return 'default';
  //   const score = typeof reputation === 'string' ? parseInt(reputation) : reputation;
  //   if (isNaN(score)) return 'default';
  //   if (score >= 70) return 'success';
  //   if (score >= 50) return 'warning';
  //   return 'error';
  // };

  const getDealStatusColor = (status: string | number | undefined) => {
    const statusStr = normalizeStatus(status).toLowerCase();
    
    switch (statusStr) {
      case 'active':
      case 'accepted':
      case 'funded':
        return 'success';
      case 'pending':
      case 'proposed':
        return 'warning';
      case 'completed':
      case 'cancelled':
        return 'info';
      case 'rejected':
      case 'breachedconsumer':
      case 'breachedprovider':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatTimestamp = (timestamp: number | bigint | undefined) => {
    if (!timestamp) return 'N/A';
    let ts = Number(timestamp);
    // If timestamp is in milliseconds (13 digits), use as is. If in seconds (10 digits), convert to ms.
    if (ts < 1e12) {
      ts = ts * 1000;
    }
    const date = new Date(ts);
    return date.toLocaleDateString();
  };

  // Helper function for features (kept for potential future use)
  // const getFeatureIcon = (feature: string, enabled: boolean) => {
  //   if (enabled) {
  //     return { icon: <CheckCircleIcon />, color: 'success' as const };
  //   }
  //   return { icon: <CancelIcon />, color: 'error' as const };
  // };

  const formatSLAValue = (value: any, type: 'latency' | 'uptime') => {
    if (!value) return 'N/A';
    
    if (type === 'latency') {
      return `${value} ms`;
    } else if (type === 'uptime') {
      const percentage = (Number(value) / 1000000).toFixed(6).replace(/\.?0+$/, '');
      return `${percentage}%`;
    }
    
    return value.toString();
  };

  const getStatusIcon = (status: string | number | undefined) => {
    const statusStr = normalizeStatus(status).toLowerCase();
    
    switch (statusStr) {
      case 'active':
      case 'accepted':
      case 'funded':
        return <CheckCircleIcon className="text-green-600" />;
      case 'pending':
      case 'proposed':
        return <WarningIcon className="text-yellow-600" />;
      case 'completed':
        return <CheckCircleIcon sx={{ color: '#617BFF' }} />;
      case 'cancelled':
        return <CancelIcon className="text-gray-600" />;
      case 'rejected':
      case 'breachedconsumer':
      case 'breachedprovider':
        return <WarningIcon className="text-red-600" />;
      default:
        return <InfoIcon className="text-gray-600" />;
    }
  };

  // Helper: get credential labels by storageType
  const getCredentialLabels = (storageType: string) => {
    const t = (storageType || 'S3').toUpperCase();
    if (t === 'S3') return { keyLabel: 'Access Key ID', secretLabel: 'Secret Access Key' };
    if (t === 'GCS') return { keyLabel: 'API Key', secretLabel: 'Secret' };
    if (t === 'BLOB') return { keyLabel: 'API Key', secretLabel: 'Secret' };
    return { keyLabel: 'API Key', secretLabel: 'Secret' };
  };

  const getStorageTypeDisplayName = (storageType: string) => {
    const t = (storageType || 'S3').toUpperCase();
    if (t === 'S3') return 'AWS S3';
    if (t === 'GCS') return 'Google Cloud Storage';
    if (t === 'BLOB') return 'Azure Blob Storage';
    return 'Storage';
  };

  // Load API key data when modal opens
  const loadApiKeyData = async () => {
    if (!detailDeal?.fb_repo_id) return;
    setApiKeyLoading(true);
    setApiKeyExisting(null);
    setApiKeyNewlyCreated(null);
    try {
      const repoRes = await withTokenRefresh(() => apiClient.getStorageRepo(detailDeal!.fb_repo_id!));
      const storageType = repoRes?.repo?.storageType || 'S3';
      setApiKeyStorageType(storageType);

      const keysRes = await withTokenRefresh(() => apiClient.getRepoKeys(detailDeal!.fb_repo_id!));
      const keys = Array.isArray(keysRes) ? keysRes : (keysRes?.keys || []);

      if (keys.length > 0) {
        const first = keys[0];
        setApiKeyExisting({ id: first.id, key: first.key });
      } else {
        const timestampString = new Date().getTime().toString();
        const createRes = await withTokenRefresh(() =>
          apiClient.createRepoKey({
            repoId: detailDeal!.fb_repo_id!,
            name: `Deal ${detailDeal!.deal_id} Key #${timestampString}`,
            accessType: ApiTypes.AccessType.WRITE,
          })
        );
        if (createRes.success && createRes.key) {
          setApiKeyNewlyCreated({ key: createRes.key, secret: createRes.secret || '' });
        } else {
          toastError('Failed to create API key');
        }
      }
    } catch (err) {
      console.error('API key load error:', err);
      toastError('Failed to load API credentials');
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleRegenerateApiKey = async () => {
    if (!detailDeal?.fb_repo_id || !apiKeyExisting) return;
    setApiKeyRegenerating(true);
    try {
      await withTokenRefresh(() => apiClient.deleteRepoKey(detailDeal!.fb_repo_id!, apiKeyExisting!.id));
      const timestampString = new Date().getTime().toString();
      const createRes = await withTokenRefresh(() =>
        apiClient.createRepoKey({
          repoId: detailDeal!.fb_repo_id!,
          name: `Deal ${detailDeal!.deal_id} Key #${timestampString}`,
          accessType: ApiTypes.AccessType.WRITE,
        })
      );
      if (createRes.success && createRes.key) {
        setApiKeyExisting(null);
        setApiKeyNewlyCreated({ key: createRes.key, secret: createRes.secret || '' });
        toastSuccess('New API key generated. Copy the secret now—it won\'t be shown again.');
      } else {
        toastError('Failed to create new API key');
      }
    } catch (err) {
      console.error('Regenerate API key error:', err);
      toastError('Failed to regenerate API key');
    } finally {
      setApiKeyRegenerating(false);
    }
  };

  useEffect(() => {
    if (apiKeyModalOpen && detailDeal?.fb_repo_id) {
      loadApiKeyData();
    }
  }, [apiKeyModalOpen, detailDeal?.fb_repo_id]);

  // Action handlers
  const handleAccept = async (deal: UnifiedDeal) => {
    if (!walletAddress) return;
    setActionLoading(true);

    try {
      if (network === 'stellar') {
        if (!stellarClient) throw new Error('Stellar client not available');
        await stellarClient.deals.setDealAccepted(
          deal.provider_id,
          deal.consumer_id,
          Number(deal.deal_id)
        );
        toastSuccess('Deal accepted successfully');
      } else if (network === 'sui') {
        // Note: SUI contract doesn't support proposal/acceptance workflow
        // Deals are created directly with payment via create_deal()
        toastError('SUI deals are created directly with payment. No acceptance step needed.');
        return;
      }
      await loadDeals();
    } catch (error) {
      console.error('Failed to accept deal:', error);
      toastError('Failed to accept deal');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (deal: UnifiedDeal) => {
    if (!walletAddress) return;
    setActionLoading(true);

    try {
      if (network === 'stellar') {
        if (!stellarClient) throw new Error('Stellar client not available');
        await stellarClient.deals.setDealRejected(
          deal.provider_id,
          deal.consumer_id,
          Number(deal.deal_id)
        );
        toastSuccess('Deal rejected');
      } else if (network === 'sui') {
        if (!suiClient) throw new Error('SUI client not available');

        const contractAddress = process.env.REACT_APP_SUI_PACKAGE_ID || '';

        // Get the Provider object ID from the provider's address
        const providerObjectId = await getProviderObjectId(deal.provider_id);

        // Create transaction to reject deal
        const tx = new SuiTransaction();

        tx.add(Commands.MoveCall({
          target: `${contractAddress}::deal::reject_deal`,
          arguments: [
            tx.object(String(deal.deal_id)),        // deal object reference
            tx.object(providerObjectId),             // provider object reference
            tx.pure.vector('u8', Array.from(new TextEncoder().encode('Rejected by provider'))), // reason as vector<u8>
          ],
        }));

        // Execute the transaction
        await signAndExecuteTransaction(
          { transaction: tx },
          {
            onSuccess: (result) => {
              console.log('Deal rejected on SUI:', result);
              toastSuccess('Deal rejected and funds refunded');
            },
            onError: (error) => {
              console.error('Failed to reject deal on SUI:', error);
              throw error;
            },
          }
        );
      }
      await loadDeals();
    } catch (error) {
      console.error('Failed to reject deal:', error);
      toastError('Failed to reject deal');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFund = (deal: UnifiedDeal) => {
    if (network === 'stellar') {
      setFundingDeal(deal);
      setFundingAmount('');
      setFundingError('');
      setFundingModalOpen(true);
      return;
    }
    if (network === 'sui') {
      setSelectedDeal(deal);
      setDepositAmount('');
      setDepositDialogOpen(true);
    }
  };

  const validateFundingAmount = (amount: string): boolean => {
    const numAmount = parseFloat(amount);
    const usdcBalance = getUSDCBalance();
    if (isNaN(numAmount) || numAmount <= 0) {
      setFundingError('Please enter a valid amount greater than 0');
      return false;
    }
    if (numAmount > usdcBalance) {
      setFundingError(`Amount exceeds your balance of ${usdcBalance.toFixed(2)} ${getUSDCAssetCode()}`);
      return false;
    }
    setFundingError('');
    return true;
  };

  const handleFundingAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFundingAmount(value);
    if (value) validateFundingAmount(value);
    else setFundingError('');
  };

  const handleFundingSubmit = async () => {
    if (!stellarClient || !fundingDeal) return;
    if (!validateFundingAmount(fundingAmount)) return;

    const amount = parseFloat(fundingAmount);
    setFundingLoading(true);
    try {
      const amount_usd = BigInt(Math.round(amount * 10000000));
      await stellarClient.deals.setDealFunded(
        fundingDeal.consumer_id,
        fundingDeal.provider_id,
        Number(fundingDeal.deal_id),
        amount_usd,
        fundingDeal.fb_repo_id || ''
      );
      toastSuccess('Deal funded successfully');
      setFundingModalOpen(false);
      setFundingDeal(null);
      setFundingAmount('');
      await loadDeals();
      await refreshStellarBalance();
    } catch (error) {
      console.error('Failed to fund deal:', error);
      toastError('Failed to fund deal. Please try again.');
    } finally {
      setFundingLoading(false);
    }
  };

  const handleCloseFundingModal = () => {
    setFundingModalOpen(false);
    setFundingDeal(null);
    setFundingAmount('');
    setFundingLoading(false);
    setFundingError('');
  };

  const validateDepositAmount = (amount: string): boolean => {
    const numAmount = parseFloat(amount);
    const usdcBalance = getUSDCBalance();
    if (isNaN(numAmount) || numAmount <= 0) {
      setDepositError('Please enter a valid amount greater than 0');
      return false;
    }
    if (network === 'stellar' && numAmount > usdcBalance) {
      setDepositError(`Amount exceeds your balance of ${usdcBalance.toFixed(2)} ${getUSDCAssetCode()}`);
      return false;
    }
    setDepositError('');
    return true;
  };

  const handleDepositAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDepositAmount(value);
    if (value) validateDepositAmount(value);
    else setDepositError('');
  };

  const handleDeposit = async () => {
    if (!walletAddress || !selectedDeal || !depositAmount) return;
    if (network === 'stellar' && !validateDepositAmount(depositAmount)) return;
    setActionLoading(true);

    try {
      const amount = Number.parseFloat(depositAmount);
      if (Number.isNaN(amount) || amount <= 0) {
        toastError('Invalid deposit amount');
        return;
      }

      if (network === 'stellar') {
        if (!stellarClient) throw new Error('Stellar client not available');
        await stellarClient.deals.depositToDeal(
          selectedDeal.consumer_id,
          selectedDeal.provider_id,
          Number(selectedDeal.deal_id),
          BigInt(Math.round(amount * 10000000)) // Convert to stroops
        );
        toastSuccess('Deposit successful');
      } else if (network === 'sui') {
        // Note: add_funds_to_escrow is public(package) only and can't be called from frontend
        // You would need to create a public wrapper function in your smart contract
        setDepositedAmount( depositedAmount + amount);
        //toastError('Additional deposits not supported. Contact support to extend deal.');
      }

      setDepositDialogOpen(false);
      setDepositAmount('');
      setDepositError('');
      setSelectedDeal(null);
      await loadDeals();
      await refreshStellarBalance();
    } catch (error) {
      console.error('Failed to deposit:', error);
      toastError('Failed to deposit');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseDepositDialog = () => {
    setDepositDialogOpen(false);
    setDepositAmount('');
    setDepositError('');
    setSelectedDeal(null);
  };

  const handleTerminate = async (deal: UnifiedDeal) => {
    if (!walletAddress) return;
    setActionLoading(true);

    await deleteRepo(deal);
    try {
      if (network === 'stellar') {
        if (!stellarClient) throw new Error('Stellar client not available');
        await stellarClient.deals.setDealCompleted(
          deal.consumer_id,
          deal.provider_id,
          Number(deal.deal_id)
        );
        toastSuccess('Deal completed');
      } else if (network === 'sui') {
        if (!suiClient) throw new Error('SUI client not available');

        const contractAddress = process.env.REACT_APP_SUI_PACKAGE_ID || '';
        
        // Get the Provider object ID from the provider's address
        const providerObjectId = await getProviderObjectId(deal.provider_id);

        // Call complete_deal to mark deal as completed and transfer funds to provider
        const tx = new SuiTransaction();

        tx.add(Commands.MoveCall({
          target: `${contractAddress}::deal::complete_deal`,
          arguments: [
            tx.object(String(deal.deal_id)), // deal object reference
            tx.object(String(providerObjectId)), // provider object reference
          ],
        }));

        // Execute the transaction
        await new Promise((resolve, reject) => {
          signAndExecuteTransaction(
            { transaction: tx },
            {
              onSuccess: (result) => {
                console.log('Deal completed on SUI:', result);
                toastSuccess('Deal completed successfully');
                resolve(result);
              },
              onError: (error) => {
                console.error('Failed to complete deal on SUI:', error);
                reject(error);
              },
            }
          );
        });
      }
      await loadDeals();
      await refreshStellarBalance();
    } catch (error) {
      console.error('Failed to terminate deal:', error);
      toastError('Failed to terminate deal');
    } finally {
      setActionLoading(false);
    }
  };

  const deleteRepo = async (deal: UnifiedDeal) => {
    if (!walletAddress) return;
    setActionLoading(true);

    try {
      console.log('!!! delete deal : deal.fb_repo_id: ', deal.fb_repo_id);
      const response = await withTokenRefresh(() => apiClient.deleteStorageRepo(deal.fb_repo_id || ''));
    } catch (error) {
      console.error('Failed to delete repo:', error);
      toastError('Failed to delete repo');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async (deal: UnifiedDeal) => {
    if (!walletAddress) return;
    setActionLoading(true);

    try {
      if (network === 'stellar') {
        if (!stellarClient) throw new Error('Stellar client not available');
        await stellarClient.deals.setDealCancelled(
          deal.consumer_id,
          deal.provider_id,
          Number(deal.deal_id)
        );
        toastSuccess('Deal cancelled');
      } else if (network === 'sui') {
        if (!suiClient) throw new Error('SUI client not available');

        const contractAddress = process.env.REACT_APP_SUI_PACKAGE_ID || '';
        
        // Get the Provider object ID from the provider's address
        const providerObjectId = await getProviderObjectId(deal.provider_id);

        const tx = new SuiTransaction();

        tx.add(Commands.MoveCall({
          target: `${contractAddress}::deal::cancel_deal`,
          arguments: [
            tx.object(String(deal.deal_id)), // deal
            tx.object(String(providerObjectId)), // provider
          ],
        }));

        await new Promise((resolve, reject) => {
          signAndExecuteTransaction(
            { transaction: tx },
            {
              onSuccess: (result) => {
                console.log('Deal cancelled on SUI:', result);
                toastSuccess('Deal cancelled successfully');
                resolve(result);
              },
              onError: (error) => {
                console.error('Failed to cancel deal on SUI:', error);
                toastError('Failed to cancel deal');
                reject(error);
              },
            }
          );
        });
      }
      await loadDeals();
      await refreshStellarBalance();
    } catch (error) {
      console.error('Failed to cancel deal:', error);
      toastError('Failed to cancel deal');
    } finally {
      setActionLoading(false);
    }
  };

  // Normalize status for comparison (contract uses Pending, UI may show Proposed)
    const isStatus = (dealStatus: string | number | undefined, expected: string) =>
    normalizeStatus(dealStatus).toLowerCase() === expected?.toLowerCase();

  // Render action buttons based on deal status and view mode (contract-aligned flow)
  const renderActions = (deal: UnifiedDeal) => {
    const isProvider = viewMode === 'provider';
    const isConsumer = viewMode === 'consumer';
    const status = deal.status;

    // Pending (contract) or Proposed (legacy): Provider Accept/Reject, Consumer read-only
    if (isStatus(status, 'Pending') || isStatus(status, 'Proposed')) {
      if (isProvider) {
        return (
          <div className="flex gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); handleAccept(deal); }}
              disabled={actionLoading}
              className="flex-1 px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 transition-colors text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {actionLoading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
              Accept
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleReject(deal); }}
              disabled={actionLoading}
              className="flex-1 px-3 py-2 rounded-lg border border-red-300 hover:bg-red-50 transition-colors text-red-600 text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {actionLoading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
              Reject
            </button>
          </div>
        );
      }
      return null; // Consumer: read-only, card click opens details
    }

    // Accepted: Consumer Fund, Provider Cancel (contract: only provider can cancel)
    if (isStatus(status, 'Accepted')) {
      if (isConsumer) {
        return (
          <button
            onClick={(e) => { e.stopPropagation(); handleFund(deal); }}
            disabled={actionLoading}
            style={{ backgroundColor: '#617BFF' }}
            className="w-full px-3 py-2 rounded-lg hover:opacity-90 transition-colors text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {actionLoading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : null}
            Fund
          </button>
        );
      }
      if (isProvider) {
        return (
          <button
            onClick={(e) => { e.stopPropagation(); handleCancel(deal); }}
            disabled={actionLoading}
            className="w-full px-3 py-2 rounded-lg border border-red-300 hover:bg-red-50 transition-colors text-red-600 text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {actionLoading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
            Cancel
          </button>
        );
      }
      return null;
    }

    // Funded / Active: Consumer Terminate + Deposit (add more), Provider read-only
    if (isStatus(status, 'Funded') || isStatus(status, 'Active')) {
      if (isConsumer) {
        return (
          <div className="flex gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); setSelectedDeal(deal); setDepositDialogOpen(true); }}
              disabled={actionLoading}
              style={{ backgroundColor: '#617BFF' }}
              className="flex-1 px-3 py-2 rounded-lg hover:opacity-90 transition-colors text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {actionLoading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : null}
              Deposit
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleTerminate(deal); }}
              disabled={actionLoading}
              className="flex-1 px-3 py-2 rounded-lg border border-amber-300 hover:bg-amber-50 transition-colors text-amber-700 text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {actionLoading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
              Terminate
            </button>
          </div>
        );
      }
      return null; // Provider: read-only
    }

    // BreachedConsumer: Consumer Deposit (top-up to cover unpaid), Provider Cancel
    if (isStatus(status, 'BreachedConsumer')) {
      if (isConsumer) {
        return (
          <div className="flex gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); setSelectedDeal(deal); setDepositDialogOpen(true); }}
              disabled={actionLoading}
              style={{ backgroundColor: '#617BFF' }}
              className="flex-1 px-3 py-2 rounded-lg hover:opacity-90 transition-colors text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {actionLoading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : null}
              Deposit
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleDealCardClick(deal); }}
              className="flex-1 px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-gray-700 text-sm font-semibold"
            >
              View Details
            </button>
          </div>
        );
      }
      if (isProvider) {
        return (
          <button
            onClick={(e) => { e.stopPropagation(); handleCancel(deal); }}
            disabled={actionLoading}
            className="w-full px-3 py-2 rounded-lg border border-red-300 hover:bg-red-50 transition-colors text-red-600 text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {actionLoading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
            Cancel
          </button>
        );
      }
      return null;
    }

    // BreachedProvider: Consumer Terminate only
    if (isStatus(status, 'BreachedProvider')) {
      if (isConsumer) {
        return (
          <button
            onClick={(e) => { e.stopPropagation(); handleTerminate(deal); }}
            disabled={actionLoading}
            className="w-full px-3 py-2 rounded-lg border border-amber-300 hover:bg-amber-50 transition-colors text-amber-700 text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {actionLoading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
            Terminate
          </button>
        );
      }
      return null;
    }

    // Completed, Cancelled, Rejected, etc.: read-only
    return (
      <button
        onClick={(e) => { e.stopPropagation(); handleDealCardClick(deal); }}
        className="w-full px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-gray-700 text-sm font-semibold"
      >
        View Details
      </button>
    );
  };

  // Render wallet connection UI if not connected
  if (!isConnected && network) {
    return (
      <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
        <DePINHeader
          title="ISP Market"
          subtitle="Sell, purchase, and manage your resources (storage units ...) provided by Infrastructure Service Providers in the marketplace of your choice."
          sectionTitle="Deals"
          sectionSubtitle="Manage your active and past resource purchases"
          network={network}
          isConnected={false}
          showAddButton={false}
        />

        <div className="flex-1 px-10 pb-6 flex items-center justify-center">
          <div style={{ maxWidth: 420, width: '100%', padding: '40px 32px', textAlign: 'center', backgroundColor: '#FFFFFF', borderRadius: 16, border: '1px solid #E9EAEB', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
            <div style={{ width: 56, height: 56, margin: '0 auto 20px', borderRadius: 14, backgroundColor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 600, color: '#181D27', marginBottom: 8 }}>Connect Your Wallet</h3>
            <p style={{ fontSize: 13, color: '#717680', marginBottom: 24, lineHeight: '20px' }}>
              You need to connect your {network === 'stellar' ? 'Stellar' : 'SUI'} wallet to view your deals.
            </p>

            {network === 'stellar' ? (
              <button
                onClick={() => connectWallet(WalletType.STELLAR)}
                disabled={walletLoading}
                style={{ padding: '10px 24px', backgroundColor: '#617BFF', color: '#FFFFFF', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background-color 0.2s' }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#5A6FE8')}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#617BFF')}
              >
                {walletLoading ? 'Connecting...' : 'Connect Wallet'}
              </button>
            ) : (
              <div className="flex justify-center">
                <ConnectButton />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render no network selected state
  if (!network) {
    return (
      <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
        <DePINHeader
          title="ISP Market"
          subtitle="Sell, purchase, and manage your resources (storage units ...) provided by Infrastructure Service Providers in the marketplace of your choice."
          sectionTitle="Deals"
          sectionSubtitle="Manage your active and past resource purchases"
          network={null}
          isConnected={false}
          showAddButton={false}
        />

        <div className="flex-1 px-10 pb-6 flex items-center justify-center">
          <div style={{ maxWidth: 420, width: '100%', padding: '40px 32px', textAlign: 'center', backgroundColor: '#FFFFFF', borderRadius: 16, border: '1px solid #E9EAEB', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
            <div style={{ width: 56, height: 56, margin: '0 auto 20px', borderRadius: 14, backgroundColor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 600, color: '#181D27', marginBottom: 8 }}>Select a Network</h3>
            <p style={{ fontSize: 13, color: '#717680', lineHeight: '20px' }}>
              Please select a blockchain network (Stellar or SUI) from the header menu to continue.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
      {/* DePIN Hub Tutorial */}
      <DePINHubTutorialLauncher
        isVisible={showTutorial}
        onDismiss={() => { /* Tutorial always visible */ }}
        stepsStatus={[
          { label: 'Connect your wallet', completed: isConnected },
          { label: 'Register a storage unit', completed: false },
          { label: 'Browse the marketplace', completed: false },
          { label: 'Create your first deal', completed: deals.length > 0 },
        ]}
        currentStep={deals.length > 0 ? 4 : 3}
      />

      {/* Deal Detail Modal */}
      <Modal
        open={dealDetailOpen}
        onClose={handleCloseDealDetail}
        aria-labelledby="deal-detail-modal"
        aria-describedby="deal-detail-description"
      >
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-11/12 max-w-6xl max-h-[90vh] bg-white overflow-hidden" style={{ borderRadius: 16, border: '1px solid #E9EAEB', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
          {/* Modal Header */}
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #E9EAEB', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
              </div>
              <div>
                <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#181D27', lineHeight: '24px' }}>Deal #{detailDeal?.deal_id} Details</Typography>
                <Typography sx={{ fontSize: '13px', color: '#717680', lineHeight: '18px' }}>View deal information, storage, and pricing</Typography>
              </div>
            </div>
            <IconButton onClick={handleCloseDealDetail} size="small" sx={{ color: '#98A2B3', '&:hover': { color: '#535862', backgroundColor: '#F3F4F6' } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          {/* Modal Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {detailDeal && (
              <div className="space-y-8">
                {/* Deal Information Section */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#181D27', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <InfoIcon sx={{ fontSize: 18, color: '#617BFF' }} />
                      Deal Information
                    </Typography>
                    <div className="flex flex-wrap gap-2">
                      {(detailDeal.api_compatibility || '').split(',').map((api, index) => (
                        <Chip
                          key={index}
                          label={api.trim()}
                          size="small"
                          sx={{ height: 22, fontSize: '11px', fontWeight: 600, backgroundColor: '#F0F2FF', color: '#617BFF', border: '1px solid #D9DEFF' }}
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* API Key Button: only for consumer when deal is Funded or BreachedProvider (post accept+fund, not ended) */}
                  {viewMode === 'consumer' && detailDeal && ['Funded','Active','BreachedProvider'].some(s => normalizeStatus(detailDeal?.status).toLowerCase() === s.toLowerCase()) && (
                    <div className="mb-6 flex items-center justify-center">
                      <Button
                        variant="contained"
                        startIcon={<KeyIcon />}
                        onClick={() => {
                          setApiKeyModalOpen(true);
                          setApiKeyVisible(false);
                        }}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '13px',
                          borderRadius: '8px',
                          backgroundColor: '#617BFF',
                          boxShadow: 'none',
                          px: 2.5,
                          '&:hover': { backgroundColor: '#5A6FE8', boxShadow: 'none' },
                        }}
                      >
                        View API Credentials
                      </Button>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Basic Deal Info */}
                    <div style={{ backgroundColor: '#F9FAFB', borderRadius: 10, padding: 16 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#181D27', mb: 1.5 }}>
                        Basic Details
                      </Typography>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Status:</span>
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(detailDeal.status)}
                            <Chip
                              label={normalizeStatus(detailDeal.status) || 'Unknown'}
                              color={getDealStatusColor(detailDeal.status) as any}
                              size="small"
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Start Date:</span>
                          <span className="font-medium text-gray-900">
                            {formatTimestamp(detailDeal.start_time)}
                          </span>
                        </div>
                        {detailDeal.end_time && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">End Date:</span>
                            <span className="font-medium text-gray-900">
                              {formatTimestamp(detailDeal.end_time)}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Consumer:</span>
                          <span className="flex items-center gap-2">
                            {formatAddress(detailDeal.consumer_id)}
                            {detailDeal.consumer_reputation != null && !Number.isNaN(detailDeal.consumer_reputation) && (
                              <ReputationControl value={detailDeal.consumer_reputation} size="sm" />
                            )}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Provider:</span>
                          <span className="flex items-center gap-2">
                            {formatAddress(detailDeal.provider_id)}
                            {detailDeal.provider_reputation != null && !Number.isNaN(detailDeal.provider_reputation) && (
                              <ReputationControl value={detailDeal.provider_reputation} size="sm" />
                            )}
                          </span>
                        </div>
                        {(detailDeal.balance_consumer !== undefined || detailDeal.balance_provider !== undefined) && (
                          <>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-600">Balance Consumer:</span>
                              <span className="font-medium text-gray-900">
                                ${(Number(detailDeal.balance_consumer ?? 0) / 1e7).toFixed(2)}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-600">Balance Provider:</span>
                              <span className="font-medium text-gray-900">
                                ${(Number(detailDeal.balance_provider ?? 0) / 1e7).toFixed(2)}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Storage and Egress Info */}
                    <div style={{ backgroundColor: '#F9FAFB', borderRadius: 10, padding: 16 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#181D27', mb: 1.5 }}>
                        Storage & Egress (1 month period)
                      </Typography>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Agreed Storage:</span>
                          <span className="font-medium text-gray-900">
                            {Number(detailDeal.agreed_storage_mb ?? 0)} MB
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Agreed Egress:</span>
                          <span className="font-medium text-gray-900">
                            {Number(detailDeal.agreed_egress_mb ?? 0)} MB
                          </span>
                        </div>
                        {detailDeal.used_storage_mb !== undefined && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Used Storage:</span>
                            <span className="font-medium text-gray-900">
                              {Number(detailDeal.used_storage_mb)} MB
                            </span>
                          </div>
                        )}
                        {detailDeal.used_egress_mb !== undefined && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Used Egress:</span>
                            <span className="font-medium text-gray-900">
                              {Number(detailDeal.used_egress_mb)} MB
                            </span>
                          </div>
                        )}
                        {detailDeal.unpaid_storage_mb !== undefined && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Unpaid Storage:</span>
                            <span className="font-medium text-gray-900">
                              {Number(detailDeal.unpaid_storage_mb)} MB
                            </span>
                          </div>
                        )}
                        {detailDeal.unpaid_egress_mb !== undefined && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Unpaid Egress:</span>
                            <span className="font-medium text-gray-900">
                              {Number(detailDeal.unpaid_egress_mb)} MB
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Pricing and SLAs */}
                  <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div style={{ backgroundColor: '#F9FAFB', borderRadius: 10, padding: 16 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#181D27', mb: 1.5 }}>
                        Pricing
                      </Typography>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Storage Price:</span>
                          <span className="font-medium text-gray-900">
                            {formatPrice(detailDeal.price_per_gb_storage)}/GB
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Egress Price:</span>
                          <span className="font-medium text-gray-900">
                            {formatPrice(detailDeal.price_per_gb_egress)}/GB
                          </span>
                        </div>
                      </div>
                    </div>

                    <div style={{ backgroundColor: '#F9FAFB', borderRadius: 10, padding: 16 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#181D27', mb: 1.5 }}>
                        Service Level Agreements
                      </Typography>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Best Latency:</span>
                          <span className="font-medium text-gray-900">
                            {formatSLAValue(detailDeal.sla_avg_latency_ms, 'latency')}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Uptime:</span>
                          <span className="font-medium text-gray-900">
                            {formatSLAValue(detailDeal.sla_avg_uptime_pct, 'uptime')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <Divider />

                {/* Storage Unit Information Section */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#181D27', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <StorageIcon sx={{ fontSize: 18, color: '#617BFF' }} />
                      Storage Unit Information
                    </Typography>
                    <div className="flex flex-wrap gap-2">
                      {(detailDeal.api_compatibility || '').split(',').map((api, index) => (
                        <Chip
                          key={index}
                          label={api.trim()}
                          size="small"
                          sx={{ height: 22, fontSize: '11px', fontWeight: 600, backgroundColor: '#F0F2FF', color: '#617BFF', border: '1px solid #D9DEFF' }}
                        />
                      ))}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Storage Unit Details */}
                    <div style={{ backgroundColor: '#F9FAFB', borderRadius: 10, padding: 16 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#181D27', mb: 1.5 }}>
                        Storage Unit Details
                      </Typography>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Storage Unit ID:</span>
                          <span className="font-medium text-gray-900">
                            #{String(detailDeal.bucket_id).slice(0, 8)}
                          </span>
                        </div>
                        {detailDeal.bucket_name && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Storage Unit Name:</span>
                            <span className="font-medium text-gray-900">
                              {detailDeal.bucket_name}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Region:</span>
                          <span className="font-medium text-gray-900">
                            {detailDeal.region || 'Unknown'}
                          </span>
                        </div>
                        {detailDeal.country && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Country:</span>
                            <span className="font-medium text-gray-900">
                              {detailDeal.country}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Additional Info */}
                    <div style={{ backgroundColor: '#F9FAFB', borderRadius: 10, padding: 16 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#181D27', mb: 1.5 }}>
                        Additional Information
                      </Typography>
                      <div className="space-y-3">
                        {detailDeal.duration_secs !== undefined && Number(detailDeal.duration_secs) > 0 ? (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Duration:</span>
                            <span className="font-medium text-gray-900">
                              {Math.floor(Number(detailDeal.duration_secs) / 86400)} days
                            </span>
                          </div>
                        ) : null}
                        {detailDeal.fb_repo_id && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Flashgate Repo ID:</span>
                            <span className="font-medium text-gray-900">
                              {detailDeal.fb_repo_id}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Funding Modal (Stellar: consumer funds deal from Accepted -> Funded) */}
      <Modal
        open={fundingModalOpen}
        onClose={handleCloseFundingModal}
        aria-labelledby="funding-modal"
        aria-describedby="funding-description"
      >
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white overflow-hidden" style={{ borderRadius: 16, border: '1px solid #E9EAEB', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
          {/* Header */}
          <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid #E9EAEB' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="1" x2="12" y2="23" />
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                </div>
                <div>
                  <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#181D27', lineHeight: '24px' }}>Fund Deal</Typography>
                  <Typography sx={{ fontSize: '13px', color: '#717680', lineHeight: '18px' }}>Deposit funds to activate this deal</Typography>
                </div>
              </div>
              <IconButton onClick={handleCloseFundingModal} size="small" sx={{ color: '#98A2B3', '&:hover': { color: '#535862', backgroundColor: '#F3F4F6' } }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          </div>

          {/* Content */}
          <div style={{ padding: '20px 24px 24px' }}>
            <Typography sx={{ fontSize: '13px', color: '#535862', mb: 2 }}>
              Enter the amount of {getUSDCAssetCode()} you want to fund this deal with:
            </Typography>

            <div style={{ marginBottom: 8 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#414651', mb: 0.75 }}>Amount ({getUSDCAssetCode()})</Typography>
              <TextField
                fullWidth
                type="number"
                value={fundingAmount}
                onChange={handleFundingAmountChange}
                placeholder="0.00"
                inputProps={{ min: '0', step: '0.01' }}
                helperText={fundingError || `Available balance: ${getUSDCBalance().toFixed(2)} ${getUSDCAssetCode()}`}
                error={!!fundingError}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: '13px',
                    '& fieldset': { borderColor: '#D0D5DD' },
                    '&:hover fieldset': { borderColor: '#98A2B3' },
                    '&.Mui-focused fieldset': { borderColor: '#617BFF', borderWidth: '1.5px' },
                  },
                  '& .MuiFormHelperText-root': { fontSize: '11px' },
                }}
              />
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <Button
                onClick={handleCloseFundingModal}
                disabled={fundingLoading}
                sx={{ textTransform: 'none', fontWeight: 500, fontSize: '13px', color: '#717680', borderRadius: '8px', px: 2, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)', color: '#535862' } }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleFundingSubmit}
                disabled={fundingLoading || !fundingAmount || parseFloat(fundingAmount) <= 0 || !!fundingError}
                startIcon={fundingLoading ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ textTransform: 'none', fontWeight: 600, fontSize: '13px', borderRadius: '8px', px: 2.5, backgroundColor: '#617BFF', boxShadow: 'none', '&:hover': { backgroundColor: '#5A6FE8', boxShadow: 'none' } }}
              >
                {fundingLoading ? 'Funding...' : 'Fund Deal'}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* API Key Modal */}
      <Modal
        open={apiKeyModalOpen}
        onClose={() => {
          setApiKeyModalOpen(false);
          setApiKeyVisible(false);
          setApiKeyExisting(null);
          setApiKeyNewlyCreated(null);
        }}
        aria-labelledby="api-key-modal"
        aria-describedby="api-key-description"
      >
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-white overflow-hidden" style={{ borderRadius: 16, border: '1px solid #E9EAEB', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
          {/* Header */}
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #E9EAEB', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <KeyIcon sx={{ fontSize: 20, color: '#617BFF' }} />
              </div>
              <div>
                <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#181D27', lineHeight: '24px' }}>API Credentials</Typography>
                <Typography sx={{ fontSize: '13px', color: '#717680', lineHeight: '18px' }}>Access keys for your storage unit</Typography>
              </div>
            </div>
            <IconButton 
              onClick={() => {
                setApiKeyModalOpen(false);
                setApiKeyVisible(false);
                setApiKeyExisting(null);
                setApiKeyNewlyCreated(null);
              }} 
              size="small"
              sx={{ color: '#98A2B3', '&:hover': { color: '#535862', backgroundColor: '#F3F4F6' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          {/* Content */}
          <div style={{ padding: '20px 24px 24px' }}>
            {detailDeal && (
              <div>
                {apiKeyLoading ? (
                  <div style={{ padding: '32px 0', textAlign: 'center' }}>
                    <CircularProgress size={24} sx={{ color: '#617BFF', mb: 1.5 }} />
                    <Typography sx={{ fontSize: '13px', color: '#717680' }}>Loading API credentials...</Typography>
                  </div>
                ) : !detailDeal.fb_repo_id ? (
                  <Typography sx={{ fontSize: '13px', color: '#717680', textAlign: 'center', py: 4 }}>
                    No repo associated with this deal. API keys are not available yet.
                  </Typography>
                ) : (apiKeyExisting || apiKeyNewlyCreated) ? (
                  <>
                    {/* Storage type badge */}
                    <div style={{ textAlign: 'center', marginBottom: 20 }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', backgroundColor: '#F0F2FF', borderRadius: 20, border: '1px solid #D9DEFF' }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#617BFF' }}>{getStorageTypeDisplayName(apiKeyStorageType)}</Typography>
                      </div>
                      <Typography sx={{ fontSize: '12px', color: '#717680', mt: 1 }}>Use these credentials to access your storage unit</Typography>
                    </div>

                    {/* Credentials card */}
                    <div style={{ backgroundColor: '#FAFBFC', border: '1px solid #E9EAEB', borderRadius: 10, padding: '20px', marginBottom: 16 }}>
                      {(() => {
                        const labels = getCredentialLabels(apiKeyStorageType);
                        const keyVal = apiKeyExisting?.key ?? apiKeyNewlyCreated?.key ?? '';
                        const secretVal = apiKeyNewlyCreated?.secret ?? (apiKeyExisting ? '••••••••••••••••' : '');
                        const secretIsPlaceholder = !!apiKeyExisting;
                        const credInputSx = {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            fontSize: '12px',
                            backgroundColor: '#FFFFFF',
                            '& fieldset': { borderColor: '#D0D5DD' },
                            '&:hover fieldset': { borderColor: '#98A2B3' },
                            '& .MuiInputBase-input': { fontFamily: 'monospace' },
                          },
                        };
                        return (
                          <>
                            <div style={{ marginBottom: 14 }}>
                              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#414651', mb: 0.5 }}>{labels.keyLabel}</Typography>
                              <TextField fullWidth value={keyVal} variant="outlined" size="small" InputProps={{ readOnly: true }} sx={credInputSx} />
                            </div>
                            <div>
                              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#414651', mb: 0.5 }}>{labels.secretLabel}</Typography>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <TextField
                                  fullWidth
                                  value={secretVal}
                                  variant="outlined"
                                  size="small"
                                  InputProps={{
                                    readOnly: true,
                                    type: apiKeyVisible && !secretIsPlaceholder ? 'text' : 'password',
                                  }}
                                  sx={credInputSx}
                                />
                                {!secretIsPlaceholder && (
                                  <IconButton onClick={() => setApiKeyVisible(!apiKeyVisible)} size="small" sx={{ color: '#717680', '&:hover': { color: '#414651', backgroundColor: '#F3F4F6' } }}>
                                    {apiKeyVisible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                  </IconButton>
                                )}
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>

                    {apiKeyExisting ? (
                      <>
                        {/* Warning: secret not available */}
                        <div style={{ padding: '12px 14px', backgroundColor: '#FFFBEB', border: '1px solid #FEF3C7', borderRadius: 8, marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                          <WarningIcon sx={{ fontSize: 16, color: '#B45309', mt: '1px', flexShrink: 0 }} />
                          <div>
                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#92400E', mb: 0.25 }}>Secret not available</Typography>
                            <Typography sx={{ fontSize: '11px', color: '#92400E', lineHeight: '16px' }}>
                              The secret is stored encrypted and cannot be retrieved. If you did not copy it when generated, you will need to generate a new API key.
                            </Typography>
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                          <Button
                            variant="outlined"
                            onClick={handleRegenerateApiKey}
                            disabled={apiKeyRegenerating}
                            startIcon={apiKeyRegenerating ? <CircularProgress size={14} color="inherit" /> : null}
                            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '12px', borderRadius: '8px', borderColor: '#D97706', color: '#D97706', '&:hover': { borderColor: '#B45309', backgroundColor: '#FFFBEB' } }}
                          >
                            {apiKeyRegenerating ? 'Generating...' : 'Generate new API key'}
                          </Button>
                          <Typography sx={{ fontSize: '11px', color: '#92400E', textAlign: 'center' }}>
                            This will invalidate the current key. Any applications using it will stop working.
                          </Typography>
                        </div>
                      </>
                    ) : (
                      <div style={{ padding: '12px 14px', backgroundColor: '#FFFBEB', border: '1px solid #FEF3C7', borderRadius: 8, marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <WarningIcon sx={{ fontSize: 16, color: '#B45309', mt: '1px', flexShrink: 0 }} />
                        <div>
                          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#92400E', mb: 0.25 }}>Copy your secret now</Typography>
                          <Typography sx={{ fontSize: '11px', color: '#92400E', lineHeight: '16px' }}>
                            The secret is shown only once. Copy it now—it will not be available on future reloads.
                          </Typography>
                        </div>
                      </div>
                    )}

                    {/* Info card */}
                    <div style={{ padding: '12px 14px', backgroundColor: '#F9FAFB', border: '1px solid #E9EAEB', borderRadius: 8, marginBottom: 20 }}>
                      <Typography sx={{ fontSize: '11px', color: '#717680', lineHeight: '16px' }}>
                        Don&apos;t share these credentials. They are valid for this deal. Use any Flashgate node to access the storage unit.
                      </Typography>
                    </div>

                    {/* Close button */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        onClick={() => {
                          setApiKeyModalOpen(false);
                          setApiKeyVisible(false);
                          setApiKeyExisting(null);
                          setApiKeyNewlyCreated(null);
                        }}
                        sx={{ textTransform: 'none', fontWeight: 600, fontSize: '13px', borderRadius: '8px', px: 3, backgroundColor: '#617BFF', boxShadow: 'none', '&:hover': { backgroundColor: '#5A6FE8', boxShadow: 'none' } }}
                      >
                        Close
                      </Button>
                    </div>
                  </>
                ) : (
                  <Typography sx={{ fontSize: '13px', color: '#717680', textAlign: 'center', py: 4 }}>
                    Failed to load API credentials. Please try again.
                  </Typography>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal>

      <DePINHeader
        title="ISP Market"
        subtitle="Sell, purchase, and manage your resources (storage units ...) provided by Infrastructure Service Providers in the Stellar marketplace."
        sectionTitle="Deals"
        sectionSubtitle="Manage your active and past resource purchases"
        network={network}
        isConnected={isConnected}
        balances={getBalances()}
        loading={loading}
        onRefresh={loadDeals}
        showAddButton={false}
        showFilterButton={true}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search deals..."
      />

      {/* Content */}
      <div className="flex-1 px-10 pb-6 overflow-y-auto">
        {/* Consumer/Provider Toggle and Filter Row */}
        <div className="flex items-center gap-6 flex-wrap mb-6">
          {/* Custom Toggle */}
          <div className="flex items-center gap-2 p-1 rounded-lg bg-gray-100">
            <button
              onClick={() => setViewMode('consumer')}
              className="px-4 py-1.5 rounded-md transition-all"
              style={{
                fontSize: '13px',
                fontWeight: 600,
                backgroundColor: viewMode === 'consumer' ? 'white' : 'transparent',
                color: viewMode === 'consumer' ? '#181D27' : '#6B7280',
                boxShadow: viewMode === 'consumer' ? '0 1px 2px rgba(0, 0, 0, 0.05)' : 'none',
              }}
            >
              As Consumer
            </button>
            <button
              onClick={() => setViewMode('provider')}
              className="px-4 py-1.5 rounded-md transition-all"
              style={{
                fontSize: '13px',
                fontWeight: 600,
                backgroundColor: viewMode === 'provider' ? 'white' : 'transparent',
                color: viewMode === 'provider' ? '#181D27' : '#6B7280',
                boxShadow: viewMode === 'provider' ? '0 1px 2px rgba(0, 0, 0, 0.05)' : 'none',
              }}
            >
              As Provider
            </button>
          </div>

          {/* Filter by Status */}
          <div className="flex items-center gap-2">
            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#6B7280', minWidth: 'fit-content' }}>
              Filter by status:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{
                  height: 32,
                  fontSize: '13px',
                  fontWeight: 500,
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#D1D5DB',
                  },
                }}
              >
                {DEAL_STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>

        {/* Deals Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#617BFF] mr-3"></div>
            <span className="text-gray-500">Loading deals...</span>
          </div>
        ) : filteredDeals.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Typography variant="h6" style={{ color: '#9CA3AF', fontWeight: 500, marginBottom: '8px' }}>
              No deals found
            </Typography>
            <Typography variant="body2" style={{ color: '#9CA3AF' }}>
              {searchQuery || statusFilter !== 'All'
                ? 'Try adjusting your search or filters'
                : viewMode === 'consumer'
                  ? 'Visit the Marketplace to purchase storage'
                  : 'Create storage units to receive deal proposals'}
            </Typography>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredDeals.map((deal) => {
              const statusStyle = getStatusColor(deal.status);
              return (
                <div
                  key={deal.deal_id}
                  className="border border-gray-200 rounded-lg p-5 bg-white hover:shadow-md transition-shadow flex flex-col cursor-pointer"
                  onClick={() => handleDealCardClick(deal)}
                >
                  {/* Header Section */}
                  <div className="mb-4 pb-4 border-b border-gray-100">
                    <div className="flex items-start justify-between mb-3">
                      <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#181D27' }}>
                        Deal #{String(deal.deal_id).slice(0, 8)}
                      </h3>
                      <Chip
                        label={normalizeStatus(deal.status) || 'Unknown'}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '11px',
                          fontWeight: 600,
                          backgroundColor: statusStyle.bg,
                          color: statusStyle.color,
                          border: `1px solid ${statusStyle.border}`,
                        }}
                      />
                    </div>

                    <div className="flex items-start gap-3">
                      {/* Region Logo */}
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 8,
                          backgroundColor: '#F0F2FF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M2 12h20" />
                          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                        </svg>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span style={{ fontSize: '13px', fontWeight: 600, color: '#181D27' }}>
                            {deal.region || 'Unknown Region'}
                          </span>
                          {deal.country && (
                            <span style={{ fontSize: '11px', color: '#9CA3AF' }}>
                              ({deal.country})
                            </span>
                          )}
                        </div>
                        <div className="mb-1.5 truncate">
                          <span style={{ fontSize: '12px', fontWeight: 500, color: '#6B7280' }}>
                            Storage Unit:{' '}
                          </span>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#617BFF' }}>
                            #{String(deal.bucket_id).slice(0, 8)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 truncate">
                          <span style={{ fontSize: '12px', fontWeight: 500, color: '#6B7280' }}>
                            {viewMode === 'consumer' ? 'Provider:' : 'Consumer:'}{' '}
                          </span>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#414651', fontFamily: 'monospace' }}>
                            {formatAddress(viewMode === 'consumer' ? deal.provider_id : deal.consumer_id)}
                          </span>
                          {(viewMode === 'consumer' ? deal.provider_reputation : deal.consumer_reputation) != null && !Number.isNaN(viewMode === 'consumer' ? deal.provider_reputation : deal.consumer_reputation) && (
                            <ReputationControl value={viewMode === 'consumer' ? (deal.provider_reputation ?? 0) : (deal.consumer_reputation ?? 0)} size="sm" showValue={false} />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Deal Details Section */}
                  <div className="mb-4 flex-1">
                    <h4 style={{ fontSize: '12px', fontWeight: 600, color: '#181D27', marginBottom: '10px' }}>
                      Deal Details
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Paid Storage</div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#414651' }}>
                          {deal.agreed_storage_mb} MB
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Paid Egress</div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#414651' }}>
                          {deal.agreed_egress_mb} MB
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Latency</div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#10B981' }}>
                          {deal.sla_avg_latency_ms}ms
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Uptime</div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#10B981' }}>
                          {formatUptime(deal.sla_avg_uptime_pct)}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>API Compatibility</div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#414651' }}>
                          {deal.api_compatibility}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Deposited Amount</div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#414651' }}>
                        ${(Number(deal.balance_consumer ?? 0) / 1e7).toFixed(2)} {getUSDCAssetCode()}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="pt-3 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
                    {renderActions(deal)}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Help Section */}
        <Box
          sx={{
            mt: 6,
            p: 4,
            borderRadius: '12px',
            border: '1px solid #E9EAEB',
            backgroundColor: '#FAFAFA',
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: '16px', lineHeight: '24px', color: '#181D27', mb: 2 }}>
            Help and Support
          </Typography>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a
              href="https://docs.flashback.network/deals/managing"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:border-[#617BFF] transition-colors bg-white"
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  backgroundColor: '#F0F2FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <div>
                <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#181D27', mb: 0.5 }}>
                  Managing deals
                </Typography>
                <Typography sx={{ fontWeight: 400, fontSize: 13, color: '#717680' }}>
                  Learn how to manage your deals
                </Typography>
              </div>
            </a>

            <a
              href="https://docs.flashback.network/deals/billing"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:border-[#617BFF] transition-colors bg-white"
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  backgroundColor: '#F0F2FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <line x1="2" y1="10" x2="22" y2="10" />
                </svg>
              </div>
              <div>
                <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#181D27', mb: 0.5 }}>
                  Billing information
                </Typography>
                <Typography sx={{ fontWeight: 400, fontSize: 13, color: '#717680' }}>
                  Understand your billing
                </Typography>
              </div>
            </a>

            <a
              href="https://docs.flashback.network/api-reference/deals"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:border-[#617BFF] transition-colors bg-white"
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  backgroundColor: '#F0F2FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                  <polyline points="16 18 22 12 16 6" />
                  <polyline points="8 6 2 12 8 18" />
                </svg>
              </div>
              <div>
                <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#181D27', mb: 0.5 }}>
                  Build with API
                </Typography>
                <Typography sx={{ fontWeight: 400, fontSize: 13, color: '#717680' }}>
                  Explore deals API
                </Typography>
              </div>
            </a>
          </div>
        </Box>
      </div>

      {/* Deposit Dialog */}
      <Dialog
        open={depositDialogOpen}
        onClose={handleCloseDepositDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', border: '1px solid #E9EAEB', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' } }}
      >
        <DialogContent sx={{ p: 0 }}>
          {/* Header */}
          <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid #E9EAEB' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              </div>
              <div>
                <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#181D27', lineHeight: '24px' }}>Deposit to Deal</Typography>
                <Typography sx={{ fontSize: '13px', color: '#717680', lineHeight: '18px' }}>Add funds to an existing deal</Typography>
              </div>
            </div>
          </div>

          {/* Content */}
          <div style={{ padding: '20px 24px 24px' }}>
            <div style={{ marginBottom: 8 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#414651', mb: 0.75 }}>Amount ({network === 'stellar' ? getUSDCAssetCode() : 'SUI'})</Typography>
              <TextField
                autoFocus
                fullWidth
                type="number"
                value={depositAmount}
                onChange={handleDepositAmountChange}
                placeholder="0.00"
                inputProps={{ min: '0', step: '0.01' }}
                helperText={depositError || (network === 'stellar' ? `Available balance: ${getUSDCBalance().toFixed(2)} ${getUSDCAssetCode()}` : undefined)}
                error={!!depositError}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: '13px',
                    '& fieldset': { borderColor: '#D0D5DD' },
                    '&:hover fieldset': { borderColor: '#98A2B3' },
                    '&.Mui-focused fieldset': { borderColor: '#617BFF', borderWidth: '1.5px' },
                  },
                  '& .MuiFormHelperText-root': { fontSize: '11px' },
                }}
              />
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <Button
                onClick={handleCloseDepositDialog}
                disabled={actionLoading}
                sx={{ textTransform: 'none', fontWeight: 500, fontSize: '13px', color: '#717680', borderRadius: '8px', px: 2, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)', color: '#535862' } }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeposit}
                variant="contained"
                disabled={actionLoading || !depositAmount || parseFloat(depositAmount) <= 0 || !!depositError}
                startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ textTransform: 'none', fontWeight: 600, fontSize: '13px', borderRadius: '8px', px: 2.5, backgroundColor: '#617BFF', boxShadow: 'none', '&:hover': { backgroundColor: '#5A6FE8', boxShadow: 'none' } }}
              >
                {actionLoading ? 'Processing...' : 'Deposit'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default DePINHubDeals;

